import React, { useRef, useState } from "react";
import { coffeeRequest, errorMessage, newRequestId } from "./coffeeApi";
import { emptyForm, payload, queryString, useResource } from "./coffeeUi";
import CoffeeFields from "./CoffeeFields";
import CoffeePeriod from "./CoffeePeriod";
import CoffeeHistory from "./CoffeeHistory";
import CoffeeDetail from "./CoffeeDetail";
import "./coffeeStatistics.css";
export default function CoffeeRegistration({
  request = coffeeRequest,
  user,
  operatorOptions,
  operatorValue,
  onOperatorChange,
}) {
  const operatorName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.email ||
    "Usuario de la sesión";
  const availableOperators = operatorOptions || [
    { value: "session", label: operatorName },
  ];
  const [refresh, setRefresh] = useState(0),
    [route, setRoute] = useState(""),
    [machine, setMachine] = useState(""),
    [form, setForm] = useState(emptyForm),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [error, setError] = useState(""),
    [page, setPage] = useState(1),
    [detail, setDetail] = useState(null);
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const identity = useRef({});
  const options = useResource("/options", request, refresh);
  const effectiveRoute =
    route || (options.data?.routes.length === 1 ? options.data.routes[0] : "");
  const machines =
    options.data?.machines.filter((item) => item.route === effectiveRoute) ||
    [];
  const selected = machines.find((item) => item._id === machine);
  const previous = useResource(
    selected
      ? `/previous/${machine}?${queryString({ route: effectiveRoute })}`
      : null,
    request,
    refresh
  );
  const history = useResource(
    `/mine?${queryString({ page, route: effectiveRoute, machine })}`,
    request,
    refresh
  );
  const clearMachine = (value) => {
    setMachine(value);
    setForm((old) => ({
      ...emptyForm(),
      from: old.from,
      until: old.until,
      fromTime: old.fromTime,
      untilTime: old.untilTime,
    }));
    setMessage("");
    setError("");
    identity.current = {};
  };
  const save = async (event) => {
    event.preventDefault();
    if (!previous.data || !selected || busy || photoProcessing) return;
    setBusy(true);
    setError("");
    setMessage("");
    const data = { ...payload(form), machine, route: effectiveRoute };
    const fingerprint = JSON.stringify(data);
    if (identity.current.fingerprint !== fingerprint)
      identity.current = { fingerprint, id: newRequestId() };
    try {
      await request(
        "",
        {
          ...data,
          expectedVersion: previous.data.version,
          requestId: identity.current.id,
        },
        "POST"
      );
      setMessage("Registro enviado correctamente.");
      setForm(emptyForm());
      identity.current = {};
      setPage(1);
      setRefresh((x) => x + 1);
    } catch (err) {
      setError(errorMessage(err));
      if (err?.response?.status === 409) setRefresh((x) => x + 1);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="coffee-page">
      <div className="coffee-heading">
        <div>
          <p className="coffee-eyebrow">CONTROL OPERATIVO</p>
          <h1>Registro de Estadísticas</h1>
          <p>Contadores, bebidas y auditorías por período.</p>
        </div>
      </div>
      {message && (
        <p className="coffee-success" role="status">
          {message}
        </p>
      )}
      {error && (
        <p className="coffee-error" role="alert">
          {error}
        </p>
      )}
      <form onSubmit={save}>
        <fieldset disabled={busy || options.loading || photoProcessing}>
          <CoffeePeriod form={form} setForm={setForm} />
          <section>
            <h2>Operador, ruta y máquina</h2>
            <p className="coffee-hint">Las rutas vinculadas usan el catálogo actual. Solo se muestran máquinas de café según tus permisos.</p>
            {options.loading && <p role="status">Cargando tus rutas…</p>}
            {options.error && (
              <p role="alert">
                {options.error}{" "}
                <button type="button" onClick={() => setRefresh((x) => x + 1)}>
                  Reintentar
                </button>
              </p>
            )}
            {options.data && !options.data.routes.length && (
              <p>No tienes máquinas de café con ruta asignada. Contacta a support.</p>
            )}
            <div className="coffee-grid">
              <label>
                Operador
                <select
                  value={operatorValue || "session"}
                  onChange={(e) => {
                    if (!onOperatorChange) return;
                    setRoute("");
                    clearMachine("");
                    setPage(1);
                    onOperatorChange(e.target.value);
                  }}
                >
                  {availableOperators.map((operator) => (
                    <option key={operator.value} value={operator.value}>
                      {operator.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Ruta *
                <select
                  required
                  value={effectiveRoute}
                  onChange={(e) => {
                    setRoute(e.target.value);
                    clearMachine("");
                    setPage(1);
                  }}
                >
                  <option value="">Seleccionar ruta</option>
                  {options.data?.routes.map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                Máquina *
                <select
                  required
                  value={machine}
                  disabled={!effectiveRoute}
                  onChange={(e) => {
                    clearMachine(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">Seleccionar máquina</option>
                  {machines.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.machine || item.uuid || item.serialNumber} ·{" "}
                      {item.serialNumber} · {item.location}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {selected && (
              <p className="coffee-hint">
                Serie: {selected.serialNumber} · Ubicación:{" "}
                {selected.location || "Sin ubicación"}
              </p>
            )}
          </section>
          {previous.loading && <p role="status">Cargando contador anterior…</p>}
          {previous.error && (
            <p role="alert">
              {previous.error}{" "}
              <button type="button" onClick={() => setRefresh((x) => x + 1)}>
                Reintentar
              </button>
            </p>
          )}
          {selected && previous.data && (
            <>
              <CoffeeFields
                form={form}
                setForm={setForm}
                previous={previous.data.counter}
                first={previous.data.counter == null}
                showPeriod={false}
                showPhoto
                maxPhotos={options.data?.maxPhotos || 1}
                photoProcessing={photoProcessing}
                onPhotoProcessingChange={setPhotoProcessing}
              />
              <div className="coffee-actions">
                <button className="coffee-primary" disabled={busy || photoProcessing}>
                  {busy ? "Enviando…" : "Enviar registro"}
                </button>
                <button type="button" onClick={() => setForm(emptyForm())}>
                  Limpiar formulario
                </button>
              </div>
            </>
          )}
        </fieldset>
      </form>
      <section>
        <h2>Mis registros enviados</h2>
        <CoffeeHistory
          resource={history}
          page={page}
          setPage={setPage}
          onDetail={setDetail}
          onRetry={() => setRefresh((x) => x + 1)}
        />
      </section>
      {detail && (
        <CoffeeDetail
          key={detail}
          id={detail}
          request={request}
          onClose={() => setDetail(null)}
          onSaved={() => setRefresh((x) => x + 1)}
        />
      )}
    </main>
  );
}
